import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { COVER_EFFECT_DEFAULTS, type CoverEffect } from 'fym-shared/src/music24/coverDoc';
import { TypedMemo } from '../../common/types';
import { useCoverEditor } from '../CoverEditorContext';
import { coverEditorMessages, effectMessages } from '../coverEditorMessages';
import { InspectorRow } from './controls/InspectorRow';
import { Segmented } from './controls/Segmented';
import { Slider } from './controls/Slider';
import { Swatches } from './controls/Swatches';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface EffectInspectorProps {
  effect: CoverEffect;
}

/**
 * The settings of the selected effect (design cover-editor d3): the strength, the colours of an
 * effect that has them, and if it sits under or over the text.
 */
const EffectInspectorComponent: React.FC<EffectInspectorProps> = ({ effect }) => {
  const intl = useIntl();
  const { state, dispatch, readOnly } = useCoverEditor();
  const { colourCount } = COVER_EFFECT_DEFAULTS[effect.type];
  const patch = useCallback(
    (change: Partial<Omit<CoverEffect, 'id' | 'type'>>, field?: string) =>
      dispatch({
        type: 'updateEffect',
        id: effect.id,
        patch: change,
        coalesce: field === undefined ? undefined : `effect:${effect.id}:${field}`,
      }),
    [dispatch, effect.id]
  );
  const setColour = useCallback(
    (index: number, colour: string) => {
      const colours = Array.from({ length: colourCount }, (_, at) => effect.colours[at] ?? '#ffffff');
      colours[index] = colour;
      patch({ colours }, `colour${index}`);
    },
    [colourCount, effect.colours, patch]
  );
  const remove = useCallback(() => dispatch({ type: 'removeEffect', id: effect.id }), [dispatch, effect.id]);
  const layers = useMemo(
    () => [
      { value: 'underText' as const, label: intl.formatMessage(coverEditorMessages.effectUnderText) },
      { value: 'overText' as const, label: intl.formatMessage(coverEditorMessages.effectOverText) },
    ],
    [intl]
  );
  const colourIndexes = useMemo(() => Array.from({ length: colourCount }, (_, index) => index), [colourCount]);
  const strengthLabel = intl.formatMessage(coverEditorMessages.effectStrength);

  return (
    <>
      <h4 className={styles.inspectorHead}>
        {intl.formatMessage(coverEditorMessages.inspectorEffect)}
        <span className={styles.inspectorSub}>{intl.formatMessage(effectMessages[effect.type].name)}</span>
        <span className={styles.grow} />
        <button type="button" className={styles.linkButton} onClick={remove} disabled={readOnly}>
          {intl.formatMessage(coverEditorMessages.effectRemove)}
        </button>
      </h4>
      <span className={styles.hint}>{intl.formatMessage(effectMessages[effect.type].hint)}</span>
      <div className={styles.inspectorGrid}>
        <InspectorRow label={strengthLabel}>
          <Slider
            label={strengthLabel}
            value={Math.round(effect.strength * 100)}
            min={0}
            max={100}
            display={String(Math.round(effect.strength * 100))}
            disabled={readOnly}
            onChange={(value) => patch({ strength: value / 100 }, 'strength')}
          />
        </InspectorRow>
        {colourIndexes.map((index) => {
          const label = intl.formatMessage(coverEditorMessages.effectColour, { number: index + 1 });
          return (
            <InspectorRow key={index} label={label}>
              <Swatches
                label={label}
                palette={state.doc.palette}
                value={effect.colours[index] ?? '#ffffff'}
                disabled={readOnly}
                onChange={(colour) => setColour(index, colour)}
              />
            </InspectorRow>
          );
        })}
        <InspectorRow label={intl.formatMessage(coverEditorMessages.effectLayer)}>
          <Segmented
            label={intl.formatMessage(coverEditorMessages.effectLayer)}
            options={layers}
            value={effect.layer}
            disabled={readOnly}
            onChange={(layer) => patch({ layer })}
          />
        </InspectorRow>
      </div>
    </>
  );
};

export const EffectInspector = TypedMemo(EffectInspectorComponent);
